<template>
    <div class="contract">
        <div class="contract__contents">
            <div class="contract__header">
                <button type="button" :class="`contract__header-item ${key} ${index}`"
                    v-for="(years, key, index) in years" @click="toggleContractYears(index, false)">
                    <span class="contract__academic-year">{{ key }}</span>
                </button>
            </div>
            <div class="contract__item" v-for="(years, key, index) in years">
                <div :class="`contract__content ${key} ${index} contract__content--no-rooms`" v-if="years.length === 0">
                    <div class="contract__no-rooms-container">
                        <span class="contract__no-rooms-title">Sold out</span>
                        <span class="contract__no-rooms-content">Unfortunately, there are no contracts available for
                            this academic year. Please register your intrest and get added to our waiting list</span>
                        <div class="wp-block-button is-style-secondary-button openform">
                            <button type="button" data-form="join_our_waiting_list" data-form-id="399"
                                class="wp-block-button__link wp-element-button">Join our waiting list</button>
                        </div>
                    </div>
                </div>
                <div class="contract__contract-container">
                    <div :class="`contract__content ${key} ${index}`" v-if="years.length > 0"
                        v-for="(contract, key) in years">
                        <div class="contract__contract">
                            <div class="contract__contract-title-container">
                                <span class="contract__contract-title">
                                    {{ contract.title }}
                                </span>
                            </div>
                            <div class="contract__contract-info-container">
                                <span class="contract__contract-price">£{{ getLowestPriceFromContract(index, key).price }}
                                    <span class="contract__contract-price-info">pppw</span>
                                </span>
                            </div>
                            <div class="contract__contract-cta-container">
                                <span class="contract__contract-start-date">{{ formatDate(contract.start_date) }} - {{
                        formatDate(contract.end_date) }}</span>
                                <span class="contract__contract-start-date">{{ getLowestPriceFromContract(index, key).hasDeposit }}</span>
                                <div class="wp-block-button is-style-primary-button openform" style="width: 100%">
                                    <a v-if="contract.book_now_url" target="_blank" :href="contract.book_now_url"
                                        class="wp-block-button__link wp-element-button">Book Now</a>
                                    <button v-else type="button" data-form="enquire_now" :data-form-id="postId"
                                        class="wp-block-button__link wp-element-button">Enquire Now</button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

    </div>
</template>

<script setup>
import { reactive, onMounted, computed, ref, readonly, watch } from 'vue';

const props = defineProps({
    years: {
        Array,
        default: () => []
    },
    currentYear: {
        String,
        default: ""
    },
    /** The room type post ID, used to load its enquiry form */
    postId: {
        Number,
        default: 0
    }
});

const years = props?.years // constant contains all the academic years and assosiated contracts 
const academicContractIndex = reactive({}); // reactive constant contains all the academic years and the number of contracts linked to each year
const contentHeight = ref(0); // proxy varible records the height of the heighest contract year tab 
const currentActiveYear = ref(0); // current active year
const hasDeposit = ref(""); // would contain message shown on frontend regarding deposit

onMounted(() => {

    /**
     * Resize observer monitors window for changes to the screen size, if it finds the current active tab to have a greater height 
     * than the current recorded height in the contentHeight varible then it will recalcuate the height of the active tab
     * 
     * If the width on the inner window is less than a laptop screen size, then it will set the content height to 300, height of a no contract tab, and 
     * panels containing contracts will resize based on the number of contracts
     */

    const observer = new ResizeObserver((entries) => {
        entries.forEach(entry => {
            const panels = document.querySelectorAll('.contract__item');
            if (panels[currentActiveYear.value].getBoundingClientRect().height > contentHeight.value && window.innerWidth > 1024) {
                contentHeight.value = getMaxContentHeight(panels[currentActiveYear.value])
            }
            if (window.innerWidth < 1024) {
                contentHeight.value = 300;
            }
        });
    });

    observer.observe(document.body);

    const activeAcademicYear = getYearIndex(years);
    currentActiveYear.value = activeAcademicYear;
    hideContractTabs(activeAcademicYear, true);
    toggleContractYears(activeAcademicYear, true)


});



/**
 * getYearIndex() --> used to get the number of contracts for each academic year, required to carry out the initial height calcuation and 
 * set the initial active tab to the tab with the most contracts.
 * 
 */

const getYearIndex = (years) => {
    let contractYears = Object.keys(years);
    for (let i = 0; i < contractYears.length; i++) {
        academicContractIndex[contractYears[i]] = getNumberOfContractsForAcademicYear(contractYears[i]);
    }
    return activePanel.value;
}


/**
 * getNumberOfContractsForAcademicYear() --> returns the number of contracts for the required year
 */
const getNumberOfContractsForAcademicYear = (year) => {
    return years[year].length;
}

/**
 * activePanel --> computed variable that returns the index of the academic year
 * matching the current active year. If no match is found it will return the
 * index of the year with the most contracts.
 */
const activePanel = computed(() => {
    let activePanel = 0;
    let prevContractNumber = 0;
    let contractArray = Object.values(academicContractIndex);
    if (contractArray.length === 0) {
        return activePanel;
    }

    if (props.currentYear) {
        for (let i = 0; i < contractArray.length; i++) {
            try {
                if (props.currentYear == Object.keys(academicContractIndex)[i].toString().split("/")[0] && contractArray[i] > 0) {
                    return i;
                }
            }
            catch (error) {
                console.error("Error parsing currentYear prop:", error);
            }
        }
    }

    for (let i = 0; i < contractArray.length; i++) {
        if (contractArray[i] >= prevContractNumber) {
            prevContractNumber = contractArray[i];
            activePanel = i;
        }
    }
    return activePanel;
});


/**
 * changeHeaderColours() --> takes in list of all headers, and new active index, it converts active header to have background colour of blue and color white, 
 * sets all no active headers to have a bacground colour of grey and a colour of blue 
 */
const changeHeaderColours = (headerItems, index) => {
    for (let i = 0; i < headerItems.length; i++) {
        if (i == index) {
            headerItems[i].classList.add("active");
        }
        else {
            headerItems[i].classList.remove("active");
        }
    }
}

/**
 * toggleContractYears() --> called from frontend, and inititates all the changes that happen on the front end after a panel header is pressed, records the new current
 * active year
 */
const toggleContractYears = (index, onMounted) => {
    let headerItems = document.querySelectorAll('.contract__header-item');
    currentActiveYear.value = index;
    changeHeaderColours(headerItems, index);
    if (!onMounted) {
        hideContractTabs(index, false);
    }
}


/** hideContractTabs() --> Takes in, index of the new active academic year and flag referencing if it has been called from onMounted function  
 * It will hide all non active contract panels, and show new active panel by call applyToggleStyles()
 * If function has been called from onmounted it will calcuate the height of the contract panel, as the active panel would be the panel with the most contracts (The Highest)
*/
const hideContractTabs = (activeAcademicYear, onMounted) => {
    const panels = document.querySelectorAll('.contract__item');
    for (let i = 0; i < panels.length; i++) {
        if (onMounted) {
            if (i != activeAcademicYear) {
                panels[i].style.display = "none";
            }
            else {
                const largestContentHeight = getMaxContentHeight(panels[i]);
                contentHeight.value = largestContentHeight;
                applyToggleStyles(panels[i]);
            }
        }
        if (!onMounted) {
            if (i != activeAcademicYear) {
                panels[i].style.display = "none";
            }
            else {
                applyToggleStyles(panels[i])
            }
        }
    }
}

/**
 * applyToggleStyles() --> +shows and adjusts height of newly activated panel
 */
const applyToggleStyles = (panel) => {
    panel.style.display = "flex";
    panel.style.minHeight = contentHeight.value + "px";
}

/**
 * getMaxContentHeight() --> gets height of element passed as a parameter
 */
const getMaxContentHeight = (panel) => {
    return panel.getBoundingClientRect().height;
}

/**
 * getLowestPriceFromContracts() --> takes in the index (the index of the year contract belongs to, index based on the academicContractIndex), 
 * contractIndex (the index of the contract within academic year), It will then return the lowest price found, for that contract. 
 * 
 */
const getLowestPriceFromContract = (index, contractIndex) => {
    let contactYears = Object.keys(years);
    let targetYear = contactYears[index];
    const priceObj = years[targetYear][contractIndex].prices;
    let lowestPrice = null;
    let depositIndex = 0;
    for (let i = 0; i < priceObj.length; i++) {
        if (lowestPrice === null) {
            lowestPrice = priceObj[i].price_per_person_per_week;
        }
        else {
            if (priceObj[i].price_per_person_per_week < lowestPrice) {
                lowestPrice = priceObj[i].price_per_person_per_week;
                depositIndex = i;
            }
        }
    }
    /**
     * checks to see if contract, has a deposit associated to contract with LowestPrice, assigns deposit value if one is found, which is later used on the frontend
     */
    let hasDeposit = "No deposit";
    if (priceObj[depositIndex].deposit_per_person !== "0.00") {
        hasDeposit = "Deposit: £" + priceObj[depositIndex].deposit_per_person
    }

    return {
        price: lowestPrice,
        hasDeposit
    };

}

/**
 * formats date as Month,Date,Year
 */
const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
}

</script>